import { useRef, useState } from 'react';
import { ContextualChatController } from 'api';
import { message } from 'components';

const POLLING_TIMEOUT = 1500;

const useExportChat = () => {
  const abortControllerRef = useRef<AbortController>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const handleError = (code?: string) => {
    setLoading(false);

    if (code === 'ERR_CANCELED') {
      return;
    }

    message.error('Unable to export chat');
  };

  const cancelExport = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = null;
    setLoading(false);
  };

  const checkExportStatus = async (taskId: string) => {
    if (!abortControllerRef.current) {
      return;
    }

    try {
      abortControllerRef.current = new AbortController();

      const result = await ContextualChatController.getExportStatus(
        taskId,
        abortControllerRef.current.signal,
      );

      switch (result.status) {
        case 'failed': {
          setLoading(false);
          message.error('Unable to export chat');
          break;
        }
        case 'completed': {
          setLoading(false);
          window.open(result.fileUrl, '_blank', 'noopener,noreferrer');
          break;
        }
        default: {
          setTimeout(() => checkExportStatus(taskId), POLLING_TIMEOUT);
          break;
        }
      }
    } catch (error) {
      setLoading(false);
      handleError(error?.code);
    }
  };

  const startExport = async (channelId: string) => {
    setLoading(true);
    try {
      abortControllerRef.current = new AbortController();
      const taskId = await ContextualChatController.getStartExportChat(
        channelId,
        abortControllerRef.current.signal,
      );
      setTimeout(() => checkExportStatus(taskId), POLLING_TIMEOUT);
    } catch (error) {
      handleError(error?.code);
    }
  };

  return { loading, startExport, cancelExport };
};

export default useExportChat;
